import { t } from '@lingui/macro';
import moment from 'moment';

import DF from '@rover/constants/src/i18n/datetime/datetimeInjected';
import { ALL_LANGS, DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import { Pet, UnitEnum } from '@rover/rsdk/src/apiClient/latest';
import { kebabifyString } from '@rover/rsdk/src/modules/Network/object-utilities';
import type {
  PetDetail,
  PetDetailForDisplay,
  PetDetailOption,
  PetDetailValueOption,
} from '@rover/types/src/Pet';
import inject from '@rover/utilities/injector';

// Returns the pet names in a comma separated list
export function petNameListify(petNames: string[]): string {
  return petNames.length > 0 ? petNames.join(', ') : '';
}

export function petNameSyndeticJoin(arr, conjunction = t`and`): string {
  let conjunctionLocalized = ` ${conjunction} `;

  if (arr.length < 3) {
    return arr.join(conjunctionLocalized);
  }

  // add serial comma before conjunction if language is 'en-us', 'en-ca', 'en-gb'
  if (ALL_LANGS.includes(inject('Rover.context.language', DEFAULT_LANG))) {
    conjunctionLocalized = `,${conjunctionLocalized}`;
  }

  return `${arr.slice(0, -1).join(', ')}${conjunctionLocalized}${arr.slice(-1)}`;
}

type PetInformationKey = {
  key: string;
  display: string;
};

// Returns keys and display text for the pet details that are stored on the Pet object
export function getPetInformationKeys(): PetInformationKey[] {
  return [
    {
      key: 'houseTrained',
      display: t`House trained?`,
    },
    {
      key: 'spayedNeutered',
      display: t`Spayed/neutered?`,
    },
    {
      key: 'playsChildren',
      display: t`Friendly with children?`,
    },
    {
      key: 'playsDogs',
      display: t`Friendly with dogs?`,
    },
    {
      key: 'playsCats',
      display: t`Friendly with cats?`,
    },
    {
      key: 'implant',
      display: t`Microchipped?`,
    },
  ];
}

// Returns a list of PetDetail objects for each property currently stored on the Pet object
// but not yet stored as a PetDetail
function getDerivedPetDetailsFromPetInformation(pet: Pet): PetDetailForDisplay[] {
  const { petDetails } = pet;

  if (!petDetails) {
    return [];
  }

  const newKeys = getPetInformationKeys() // Only return properties where a corresponding PetDetail doesn't exist
    .filter((detail) => !petDetails.some((pd) => pd.slug === kebabifyString(detail.key))) // Only return properties where the value is not null
    .filter(
      (detail) => Object.prototype.hasOwnProperty.call(pet, detail.key) && pet[detail.key] !== null
    );
  return newKeys.map((detail) => {
    const value = pet[detail.key] ? 'yes' : 'no';
    return {
      slug: kebabifyString(detail.key),
      value,
      displayName: detail.display,
      valueDisplayName: `${value[0].toUpperCase()}${value.slice(1).toLowerCase()}`,
      additionalInformation: pet[`${detail.key}Desc`] || '',
    };
  });
}

// Returns a list of PetDetail objects from the property or derived from PetInformation
export function getPetDetailsFromPet(pet: Pet): PetDetailForDisplay[] {
  const { petDetails } = pet;
  const derivedPetDetails = getDerivedPetDetailsFromPetInformation(pet);
  return [...derivedPetDetails, ...((petDetails as PetDetailForDisplay[]) || [])];
}
export function createPetDetailFromOption(
  value: string,
  petDetailOption: PetDetailOption,
  additionalInformation = ''
): PetDetail {
  const { slug, displayName, serviceTypes, multiselect } = petDetailOption;
  const { displayName: valueDisplayName } =
    petDetailOption.options.find((o: PetDetailValueOption) => o.value === value) || {};
  return {
    slug,
    value,
    additionalInformation,
    displayName,
    serviceTypes,
    multiselect,
    valueDisplayName: valueDisplayName || '',
  };
}

export function getPetOpkFromUrl(url: string): string {
  const petOpk = url.match(/pets\/([a-zA-Z0-9]+)\//);
  return petOpk ? petOpk[1] : '';
}

export const createVitalsFromPetInfo = (
  pet: Pet,
  weightUnit: UnitEnum | undefined = UnitEnum.lbs
): string => {
  const petGender = pet.gender === 'm' ? t`Male` : t`Female`;

  const weight = pet.weightObj.find((w) => w.unit === weightUnit)?.value ?? pet.weight;
  const petWeight = [weight, weightUnit].filter(Boolean).join(' '); // Doesn't need to be translated because of the dynamic unit

  const { ageYears, ageMonths } = pet;
  const petMonths = ageMonths ? t`${ageMonths} mo` : undefined;
  let petYearsString = ageYears && ageMonths ? t`${ageYears} yrs` : undefined;
  if (ageYears && !ageMonths) {
    petYearsString = ageYears === 1 ? t`${ageYears} year old` : t`${ageYears} years old`;
  }
  const petAge = [petYearsString, petMonths].filter(Boolean).join(' ');

  if (pet.isMemorialized) {
    if (!pet.memorialDate) return t`Passed • ${petAge}`;

    const passedDate = moment(pet.memorialDate, DF.DATE_ISO).format(DF.MONTH_YEAR_MED);
    return t`Passed ${passedDate} • ${petAge}`;
  }
  return [petGender, petWeight, petAge].filter(Boolean).join(', ');
};

const pets = {
  petNameListify,
  petNameSyndeticJoin,
  getPetInformationKeys,
  getPetDetailsFromPet,
  createPetDetailFromOption,
  getPetOpkFromUrl,
  createVitalsFromPetInfo,
};

export default pets;
